import { type IntelligenceAsset, type LineageGraph } from './intelligence'

// ---------------------------------------------------------------------------
// dbt Cloud Discovery API client
//
// The frontend calls the GraphQL Discovery API through a proxy. In local
// development the Vite dev server proxies /api/intelligence/dbt-cloud/graphql
// to dbt Cloud. In production the deployed web app service proxies the same
// path, keeping the dbt Cloud API token server-side.
//
// Required env vars:
//   VITE_DBT_CLOUD_ENVIRONMENT_ID — dbt Cloud environment ID
// ---------------------------------------------------------------------------

const DBT_CLOUD_ENVIRONMENT_ID = import.meta.env
  .VITE_DBT_CLOUD_ENVIRONMENT_ID as string | undefined

interface GraphQLRequest {
  query: string
  variables?: Record<string, unknown>
}

interface GraphQLResponse<T> {
  data?: T
  errors?: Array<{ message: string }>
}

async function graphqlRequest<T>(body: GraphQLRequest): Promise<T> {
  if (!DBT_CLOUD_ENVIRONMENT_ID) {
    throw new Error(
      'VITE_DBT_CLOUD_ENVIRONMENT_ID is not set. It is required for dbt Cloud Discovery API calls.'
    )
  }

  const response = await fetch('/api/intelligence/dbt-cloud/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(
      `dbt Cloud Discovery API request failed: ${response.status} ${text.slice(0, 500)}`
    )
  }

  const json = (await response.json()) as GraphQLResponse<T>
  if (json.errors?.length) {
    throw new Error(
      `dbt Cloud Discovery API error: ${json.errors.map((e) => e.message).join(', ')}`
    )
  }
  if (json.data === undefined) {
    throw new Error('dbt Cloud Discovery API returned no data.')
  }
  return json.data
}

// ---------------------------------------------------------------------------
// GraphQL queries and fragments
// ---------------------------------------------------------------------------

// Fields are inlined rather than pulled in via named fragments (e.g.
// "...ModelFields"). The dbt Cloud Discovery API silently fails to resolve
// fragment-spread fields on these definition node types — every field beyond
// uniqueId/resourceType (including parents/children) comes back null/empty —
// even though the identical field selection inlined directly works. This was
// verified against the live API, so fragments must be avoided here.
export const COMPILED_CODE_QUERY = `
  query Applied($environmentId: BigInt!) {
    environment(id: $environmentId) {
      applied {
        models(first: 500) {
          edges {
            node {
              uniqueId
              compiledCode
            }
          }
        }
      }
    }
  }
`

// ---------------------------------------------------------------------------
// Model historical runs — used to calculate update frequency
// ---------------------------------------------------------------------------

export const HISTORICAL_RUNS_QUERY = `
  query HistoricalRuns($environmentId: BigInt!, $uniqueId: String!, $lastRunCount: Int!) {
    environment(id: $environmentId) {
      applied {
        modelHistoricalRuns(
          uniqueId: $uniqueId
          lastRunCount: $lastRunCount
        ) {
          runGeneratedAt
          status
        }
      }
    }
  }
`

export const APPLIED_EXECUTION_QUERY = `
  query AppliedExecution($environmentId: BigInt!) {
    environment(id: $environmentId) {
      applied {
        models(first: 500) {
          edges {
            node {
              uniqueId
              executionInfo {
                lastRunGeneratedAt
                executeCompletedAt
                executionTime
                lastRunStatus
              }
            }
          }
        }
      }
    }
  }
`

interface ExecutionInfo {
  lastRunGeneratedAt?: string | null
  executeCompletedAt?: string | null
  executionTime?: number | null
  lastRunStatus?: string | null
}

interface AppliedExecutionData {
  environment?: {
    applied?: {
      models?: {
        edges?: Array<{
          node: { uniqueId: string; executionInfo?: ExecutionInfo | null }
        }>
      }
    }
  }
}

interface HistoricalRunsData {
  environment?: {
    applied?: {
      modelHistoricalRuns?: Array<{
        runGeneratedAt?: string | null
        status?: string | null
      }>
    }
  }
}

/** Human-readable update frequency derived from run history. */
export interface UpdateFrequencyMap {
  /** Maps model uniqueId → frequency label (e.g. "Every 6 hours") */
  frequencies: Map<string, string>
  /** Maps model uniqueId → last run timestamp ISO string */
  lastRun: Map<string, string>
}

/**
 * Convert a duration in milliseconds to a concise human-readable label.
 * Examples: "Every 30 min", "Every 6 hours", "Every 2 days", "Weekly"
 */
export function formatFrequency(avgMs: number): string {
  const mins = avgMs / (1000 * 60)
  if (mins < 90) return `Every ${Math.round(mins)} min`
  const hours = avgMs / (1000 * 60 * 60)
  const days = avgMs / (1000 * 60 * 60 * 24)
  if (days >= 0.8 && days < 1.5) return 'Daily'
  if (hours < 36) return `Every ${Math.round(hours)} hours`
  if (days < 10) return `Every ${Math.round(days)} days`
  if (days < 21) return 'Weekly'
  if (days < 45) return 'Monthly'
  return `Every ${Math.round(days)} days`
}

export const ENVIRONMENT_QUERY = `
  query Environment($environmentId: BigInt!) {
    environment(id: $environmentId) {
      definition {
        models(first: 500) {
          edges {
            node {
              uniqueId
              name
              resourceType
              description
              schema
              database
              parents { uniqueId name resourceType }
              children { uniqueId name resourceType }
            }
          }
        }
        sources(first: 500) {
          edges {
            node {
              uniqueId
              name
              resourceType
              description
              schema
              database
              sourceName
              children { uniqueId name resourceType }
            }
          }
        }
        seeds(first: 500) {
          edges {
            node {
              uniqueId
              name
              resourceType
              description
              schema
              database
              children { uniqueId name resourceType }
            }
          }
        }
        semanticModels(first: 500) {
          edges {
            node {
              uniqueId
              name
              resourceType
              description
              parents { uniqueId name resourceType }
              children { uniqueId name resourceType }
            }
          }
        }
        metrics(first: 500) {
          edges {
            node {
              uniqueId
              name
              resourceType
              description
              type
              label
              filter
              typeParams
              parents { uniqueId name resourceType }
              children { uniqueId name resourceType }
            }
          }
        }
      }
    }
  }
`

// ---------------------------------------------------------------------------
// Column-level lineage
//
// The Discovery API exposes catalog columns on applied models/sources but
// does not expose column-level parent relationships through the GraphQL
// schema. We fetch catalog columns for every applied model/source, then use
// the already-fetched model-level lineage (parents from the definition
// endpoint) to infer column-level lineage by matching column names
// (case-insensitive) across connected models.
// ---------------------------------------------------------------------------

export const COLUMN_LINEAGE_QUERY = `
  query ColumnLineage($environmentId: BigInt!) {
    environment(id: $environmentId) {
      applied {
        models(first: 500) {
          edges {
            node {
              uniqueId
              name
              catalog {
                columns {
                  name
                  type
                  description
                }
              }
            }
          }
        }
        sources(first: 500) {
          edges {
            node {
              uniqueId
              name
              catalog {
                columns {
                  name
                  type
                  description
                }
              }
            }
          }
        }
      }
    }
  }
`

// ---------------------------------------------------------------------------
// Domain types from the Discovery API
// ---------------------------------------------------------------------------

export interface DbtNode {
  uniqueId: string
  name: string
  resourceType: string
  description?: string | null
  schema?: string | null
  database?: string | null
  sourceName?: string | null
  tags?: Array<{ name: string } | string> | null
  // Metric-specific fields (only present on metric nodes).
  // filter and typeParams are opaque JSONObject scalars from the Discovery API.
  type?: string | null
  label?: string | null
  filter?: Record<string, unknown> | null
  typeParams?: Record<string, unknown> | null
  parents?: Array<{
    uniqueId: string
    name: string
    resourceType: string
  }> | null
  children?: Array<{
    uniqueId: string
    name: string
    resourceType: string
  }> | null
}

export interface DbtCloudDiscoveryData {
  environment?: {
    definition?: {
      models?: { edges?: Array<{ node: DbtNode }> }
      sources?: { edges?: Array<{ node: DbtNode }> }
      seeds?: { edges?: Array<{ node: DbtNode }> }
      semanticModels?: { edges?: Array<{ node: DbtNode }> }
      metrics?: { edges?: Array<{ node: DbtNode }> }
    }
    applied?: {
      models?: {
        edges?: Array<{
          node: { uniqueId: string; compiledCode?: string | null }
        }>
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Column-level lineage types
// ---------------------------------------------------------------------------

export interface CatalogColumn {
  name: string
  type?: string | null
  description?: string | null
}

interface CatalogNodeData {
  uniqueId: string
  name: string
  catalog?: { columns?: CatalogColumn[] } | null
}

interface ColumnLineageData {
  environment?: {
    applied?: {
      models?: { edges?: Array<{ node: CatalogNodeData }> }
      sources?: { edges?: Array<{ node: CatalogNodeData }> }
    }
  }
}

/** A single column's upstream parent reference. */
export interface ColumnParent {
  /** uniqueId of the parent model/source */
  nodeId: string
  /** Display name of the parent model/source */
  nodeName: string
  /** Column name in the parent */
  column: string
}

/** Column lineage entry for one column in a model. */
export interface ColumnLineageEntry {
  name: string
  type?: string | null
  description?: string | null
  parents: ColumnParent[]
}

/** Column lineage map: model uniqueId -> column lineage entries. */
export type ColumnLineageMap = Map<string, ColumnLineageEntry[]>

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

function flattenNodes(nodeList?: {
  edges?: Array<{ node: DbtNode }>
}): DbtNode[] {
  return nodeList?.edges?.map((edge) => edge.node) ?? []
}

function dedupeNodes(nodes: DbtNode[]): DbtNode[] {
  const seen = new Set<string>()
  return nodes.filter((node) => {
    if (seen.has(node.uniqueId)) return false
    seen.add(node.uniqueId)
    return true
  })
}

export function nodeType(node: DbtNode): string {
  if (node.resourceType === 'source') return 'source'
  if (node.resourceType === 'seed') return 'seed'
  if (node.resourceType === 'semantic_model') return 'semantic'
  if (node.resourceType === 'metric') return 'metric'

  const tags =
    node.tags?.map((t) =>
      typeof t === 'string' ? t.toLowerCase() : (t.name ?? '').toLowerCase()
    ) ?? []
  if (tags.includes('bronze')) return 'bronze'
  if (tags.includes('silver')) return 'silver'
  if (tags.includes('gold')) return 'gold'

  const lower = (node.name ?? '').toLowerCase()
  if (
    lower.startsWith('dim_') ||
    lower.startsWith('fact_') ||
    lower.startsWith('report_')
  ) {
    return 'gold'
  }
  if (lower.startsWith('stg_')) return 'silver'
  if (lower.startsWith('brz_') || lower.startsWith('bronze_')) return 'bronze'

  return 'silver'
}

function nodeSystem(node: DbtNode): string {
  if (node.sourceName) return node.sourceName
  return node.schema ?? node.database ?? 'dbt'
}

function nodeClassification(node: DbtNode): string {
  if (node.resourceType === 'source') return 'external source'
  if (node.resourceType === 'seed') return 'reference seed'
  if (node.resourceType === 'semantic_model') return 'semantic model'
  if (node.resourceType === 'metric') return 'metric'
  return 'dbt model'
}

function metricMetadata(node: DbtNode): Record<string, string> {
  const meta: Record<string, string> = {}
  if (node.description) meta.description = node.description
  if (node.type) meta.metricType = node.type
  if (node.label) meta.label = node.label
  // typeParams and filter are opaque JSONObject scalars from the Discovery API.
  const tp = node.typeParams as Record<string, unknown> | null | undefined
  const measure = (tp?.measure as Record<string, unknown> | undefined)?.name
  if (typeof measure === 'string' && measure) meta.measure = measure
  const expr = tp?.expr
  if (typeof expr === 'string' && expr) meta.expression = expr
  const filterObj = node.filter as Record<string, unknown> | null | undefined
  const whereList = filterObj?.where as
    | Array<Record<string, unknown>>
    | undefined
  const filters = whereList
    ?.map((w) => w.whereSqlTemplate)
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' AND ')
  if (filters) meta.filter = filters
  return meta
}

function buildLineageGraph(nodes: DbtNode[]): LineageGraph {
  const nodeMap = new Map(nodes.map((n) => [n.uniqueId, n]))
  const edgeSet = new Set<string>()
  const resultEdges: LineageGraph['edges'] = []

  const addEdge = (source: string, target: string, type: string) => {
    const key = `${source}->${target}`
    if (edgeSet.has(key)) return
    edgeSet.add(key)
    resultEdges.push({ source, target, transformation_type: type })
  }

  for (const node of nodes) {
    for (const parent of node.parents ?? []) {
      if (!nodeMap.has(parent.uniqueId)) continue
      addEdge(
        parent.uniqueId,
        node.uniqueId,
        parent.resourceType === 'source' ? 'source' : 'ref'
      )
    }
    for (const child of node.children ?? []) {
      if (!nodeMap.has(child.uniqueId)) continue
      addEdge(
        node.uniqueId,
        child.uniqueId,
        node.resourceType === 'source' ? 'source' : 'ref'
      )
    }
  }

  const resultNodes = nodes.map((node) => ({
    id: node.uniqueId,
    name: node.name,
    type: nodeType(node),
    system: nodeSystem(node),
    ...(node.resourceType === 'metric'
      ? { metadata: metricMetadata(node) }
      : {}),
  }))

  return { nodes: resultNodes, edges: resultEdges }
}

function primaryNodes(data: DbtCloudDiscoveryData): DbtNode[] {
  const definition = data.environment?.definition
  if (!definition) return []
  const primary = [
    ...flattenNodes(definition.models),
    ...flattenNodes(definition.sources),
    ...flattenNodes(definition.seeds),
    ...flattenNodes(definition.semanticModels),
    ...flattenNodes(definition.metrics),
  ]
  return dedupeNodes(primary)
}

function allNodes(data: DbtCloudDiscoveryData): DbtNode[] {
  const primary = primaryNodes(data)
  const related = collectRelated(primary)
  return dedupeNodes([...primary, ...related])
}

// The Discovery API's parents/children lists include every node type that
// references or is referenced by a model/source/seed/semantic model, not just
// other lineage nodes — e.g. dbt tests and macros. Those aren't part of the
// data transformation/semantic lineage, so they're excluded here to keep the
// lineage graph limited to real data, semantic-layer, and metric assets.
const LINEAGE_RESOURCE_TYPES = new Set([
  'model',
  'source',
  'seed',
  'semantic_model',
  'metric',
])

function collectRelated(nodes: DbtNode[]): DbtNode[] {
  const nodeMap = new Map(nodes.map((n) => [n.uniqueId, n]))
  const related: DbtNode[] = []
  for (const node of nodes) {
    for (const parent of node.parents ?? []) {
      if (!LINEAGE_RESOURCE_TYPES.has(parent.resourceType)) continue
      if (!nodeMap.has(parent.uniqueId)) related.push(parent as DbtNode)
    }
    for (const child of node.children ?? []) {
      if (!LINEAGE_RESOURCE_TYPES.has(child.resourceType)) continue
      if (!nodeMap.has(child.uniqueId)) related.push(child as DbtNode)
    }
  }
  return related
}

export function transformAssets(
  data: DbtCloudDiscoveryData
): IntelligenceAsset[] {
  return primaryNodes(data)
    .filter((node) => node.uniqueId && node.name)
    .map((node) => ({
      id: node.uniqueId,
      name: node.name,
      type: nodeType(node),
      owner: nodeSystem(node),
      classification: nodeClassification(node),
    }))
}

export function transformLineage(data: DbtCloudDiscoveryData): LineageGraph {
  const nodes = allNodes(data)
  return buildLineageGraph(nodes)
}

// Walks a single direction (ancestors via "parents", or descendants via
// "children") from a root node. Only following one direction at each step
// keeps the traversal to true ancestors/descendants — following both
// directions at every node would leak in unrelated siblings that merely
// share a common ancestor (e.g. two models fed by the same seed).
function traverseDirection(
  nodeMap: Map<string, DbtNode>,
  rootId: string,
  direction: 'parents' | 'children'
): DbtNode[] {
  const visited = new Set<string>()
  const queue: string[] = [rootId]
  const result: DbtNode[] = []

  while (queue.length) {
    const id = queue.shift() as string
    if (visited.has(id)) continue
    visited.add(id)

    const node = nodeMap.get(id)
    if (!node) continue
    result.push(node)

    for (const next of node[direction] ?? []) {
      if (!LINEAGE_RESOURCE_TYPES.has(next.resourceType)) continue
      if (!visited.has(next.uniqueId)) queue.push(next.uniqueId)
    }
  }

  return result
}

// Walks the full upstream ancestor chain and downstream descendant chain from
// a root node (not just its direct parents/children), so an asset's lineage
// reflects every transformation that ultimately feeds it or consumes it.
function traverseLineage(
  nodeMap: Map<string, DbtNode>,
  rootId: string
): DbtNode[] {
  return [
    ...traverseDirection(nodeMap, rootId, 'parents'),
    ...traverseDirection(nodeMap, rootId, 'children'),
  ]
}

export function transformAssetLineage(
  data: DbtCloudDiscoveryData,
  assetId: string
): LineageGraph {
  const nodes = allNodes(data)
  const nodeMap = new Map(nodes.map((n) => [n.uniqueId, n]))
  if (!nodeMap.has(assetId)) {
    return { asset_id: assetId, nodes: [], edges: [] }
  }

  const related = traverseLineage(nodeMap, assetId)

  return {
    asset_id: assetId,
    ...buildLineageGraph(dedupeNodes(related)),
  }
}

// ---------------------------------------------------------------------------
// Column-level lineage builder
//
// Uses model-level parent relationships (from the definition query) together
// with catalog columns (from the applied query) to infer column-level lineage.
// A column in model B is linked to a column in parent model A when:
//   1. A is a model-level parent of B (via the definition query), AND
//   2. A has a catalog column with the same name (case-insensitive).
// ---------------------------------------------------------------------------

export function buildColumnLineageMap(
  definitionData: DbtCloudDiscoveryData,
  columnData: ColumnLineageData
): ColumnLineageMap {
  // Build a global index: uniqueId -> { name, columns (lowercased name -> original) }
  const catalogIndex = new Map<
    string,
    { name: string; columns: Map<string, CatalogColumn> }
  >()

  const appliedModels =
    columnData.environment?.applied?.models?.edges ?? []
  const appliedSources =
    columnData.environment?.applied?.sources?.edges ?? []

  for (const edge of [...appliedModels, ...appliedSources]) {
    const { uniqueId, name, catalog } = edge.node
    const columns = new Map<string, CatalogColumn>()
    for (const col of catalog?.columns ?? []) {
      columns.set(col.name.toLowerCase(), col)
    }
    catalogIndex.set(uniqueId, { name, columns })
  }

  // Build model-level parent map from the definition query
  const definitionNodes = primaryNodes(definitionData)
  const parentMap = new Map<string, Array<{ uniqueId: string; name: string }>>()
  for (const node of definitionNodes) {
    const parents = (node.parents ?? [])
      .filter((p) => LINEAGE_RESOURCE_TYPES.has(p.resourceType))
      .map((p) => ({ uniqueId: p.uniqueId, name: p.name }))
    if (parents.length > 0) {
      parentMap.set(node.uniqueId, parents)
    }
  }

  // For each model with catalog columns, match columns against parent models
  const result: ColumnLineageMap = new Map()

  for (const [nodeId, catalogEntry] of catalogIndex) {
    const parents = parentMap.get(nodeId) ?? []
    const entries: ColumnLineageEntry[] = []

    for (const [lowerName, col] of catalogEntry.columns) {
      const columnParents: ColumnParent[] = []

      for (const parent of parents) {
        const parentCatalog = catalogIndex.get(parent.uniqueId)
        if (!parentCatalog) continue
        const parentCol = parentCatalog.columns.get(lowerName)
        if (parentCol) {
          columnParents.push({
            nodeId: parent.uniqueId,
            nodeName: parentCatalog.name,
            column: parentCol.name,
          })
        }
      }

      entries.push({
        name: col.name,
        type: col.type,
        description: col.description,
        parents: columnParents,
      })
    }

    result.set(nodeId, entries)
  }

  return result
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function fetchEnvironment() {
  return graphqlRequest<DbtCloudDiscoveryData>({
    query: ENVIRONMENT_QUERY,
    variables: { environmentId: Number(DBT_CLOUD_ENVIRONMENT_ID) },
  })
}

function fetchCompiledCode(uniqueId: string): Promise<string | null> {
  return graphqlRequest<DbtCloudDiscoveryData>({
    query: COMPILED_CODE_QUERY,
    variables: { environmentId: Number(DBT_CLOUD_ENVIRONMENT_ID) },
  }).then((data) => {
    const models = data.environment?.applied?.models?.edges ?? []
    const match = models.find((edge) => edge.node.uniqueId === uniqueId)
    return match?.node.compiledCode ?? null
  })
}

function fetchColumnLineage(): Promise<ColumnLineageMap> {
  return Promise.all([
    graphqlRequest<DbtCloudDiscoveryData>({
      query: ENVIRONMENT_QUERY,
      variables: { environmentId: Number(DBT_CLOUD_ENVIRONMENT_ID) },
    }),
    graphqlRequest<ColumnLineageData>({
      query: COLUMN_LINEAGE_QUERY,
      variables: { environmentId: Number(DBT_CLOUD_ENVIRONMENT_ID) },
    }),
  ]).then(([definitionData, columnData]) =>
    buildColumnLineageMap(definitionData, columnData)
  )
}

/**
 * Fetch execution info for all models and return a frequency map.
 * Uses last-run timestamps to populate the lastRun map. For the selected
 * model, we also fetch historical runs to compute actual average frequency.
 */
async function fetchUpdateFrequency(
  selectedModelId?: string
): Promise<UpdateFrequencyMap> {
  const frequencies = new Map<string, string>()
  const lastRun = new Map<string, string>()

  const execData = await graphqlRequest<AppliedExecutionData>({
    query: APPLIED_EXECUTION_QUERY,
    variables: { environmentId: Number(DBT_CLOUD_ENVIRONMENT_ID) },
  })

  const models = execData.environment?.applied?.models?.edges ?? []
  for (const { node } of models) {
    const ts =
      node.executionInfo?.executeCompletedAt ??
      node.executionInfo?.lastRunGeneratedAt
    if (ts) lastRun.set(node.uniqueId, ts)
  }

  // For the selected model, fetch historical runs to calculate frequency
  if (selectedModelId) {
    try {
      const histData = await graphqlRequest<HistoricalRunsData>({
        query: HISTORICAL_RUNS_QUERY,
        variables: {
          environmentId: Number(DBT_CLOUD_ENVIRONMENT_ID),
          uniqueId: selectedModelId,
          lastRunCount: 20,
        },
      })

      const runs = (
        histData.environment?.applied?.modelHistoricalRuns ?? []
      )
        .filter((r) => r.runGeneratedAt)
        .map((r) => new Date(r.runGeneratedAt!).getTime())
        .sort((a, b) => b - a)

      if (runs.length >= 2) {
        const intervals: number[] = []
        for (let i = 0; i < runs.length - 1; i++) {
          intervals.push(runs[i] - runs[i + 1])
        }
        const avgMs =
          intervals.reduce((sum, v) => sum + v, 0) / intervals.length
        frequencies.set(selectedModelId, formatFrequency(avgMs))
      }
    } catch {
      // Historical runs may not be available — frequency will just be absent
    }
  }

  return { frequencies, lastRun }
}

export const dbtCloudApi = {
  listAssets: () => fetchEnvironment().then(transformAssets),
  getLineage: () => fetchEnvironment().then(transformLineage),
  getAssetLineage: (assetId: string) =>
    fetchEnvironment().then((data) => transformAssetLineage(data, assetId)),
  getCompiledCode: fetchCompiledCode,
  getColumnLineage: fetchColumnLineage,
  getUpdateFrequency: fetchUpdateFrequency,
}
